import { add, isPast, isFuture, isSameDay, isSameMonth, sub } from "date-fns";
import isPlainObject from "is-plain-object";
import { Session, SessionStatus, SessionTiming } from "types";

export const isMobile = (() => {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.userAgent !== "string"
  ) {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

// Recursively removes any object keys with a value of undefined
export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj;

  const target: { [name: string]: any } = {};

  for (const key in obj) {
    const val = obj[key];
    if (typeof val !== "undefined") {
      target[key] = removeUndefineds(val);
    }
  }

  return target as T;
}

export async function getDeviceInfo() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter(device => device.kind === "audioinput"),
    videoInputDevices: devices.filter(device => device.kind === "videoinput"),
    audioOutputDevices: devices.filter(device => device.kind === "audiooutput"),
    hasAudioInputDevices: devices.some(device => device.kind === "audioinput"),
    hasVideoInputDevices: devices.some(device => device.kind === "videoinput")
  };
}

// This function will return 'true' when the specified permission has been denied by the user.
// If the API doesn't exist, or the query function returns an error, 'false' will be returned.
export async function isPermissionDenied(name: PermissionName) {
  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name });
      return result.state === "denied";
    } catch {
      return false;
    }
  } else {
    return false;
  }
}

export const SESSION_GRACE_PERIOD = 15;
export const UPCOMING_SESSION_PERIOD = 5;

export const isActiveSession = (session: Session): boolean =>
  isPast(
    sub(new Date(session.start_time), { minutes: SESSION_GRACE_PERIOD })
  ) &&
  isFuture(add(new Date(session.end_time), { minutes: SESSION_GRACE_PERIOD }))
  && !isCancelledSession(session)
  && !isCompletedSession(session);

export const isUpcomingSession = (session: Session): boolean =>
  isPast(
    sub(new Date(session.start_time), { minutes: SESSION_GRACE_PERIOD })
  ) &&
  isFuture(sub(new Date(session.end_time), {minutes: UPCOMING_SESSION_PERIOD}))
  && !isCancelledSession(session);

export const isPastSession = (session: Session): boolean =>
  isPast(add(new Date(session.end_time), { minutes: SESSION_GRACE_PERIOD }))
  && !isCancelledSession(session);

export const isFutureSession = (session: Session): boolean =>
  isFuture(
    sub(new Date(session.start_time), { minutes: SESSION_GRACE_PERIOD })
  ) && !isCancelledSession(session);

  export const isSameDaySession = (session: Session): boolean =>
  isSameDay(
    new Date(session.start_time), new Date()
  ) && !isCancelledSession(session);

  export const isSameMonthSession = (session: Session): boolean =>
    isSameDay(
      new Date(session.start_time), new Date()
    );

export const isCancellableSession = (session: Session): boolean =>
  isFuture(
    add(new Date(session.start_time), { minutes: SESSION_GRACE_PERIOD })
  ) &&
  !isCancelledSession(session) &&
  !isCompletedSession(session);

export const getSessionTiming = (session: Session): SessionTiming => {
  if (isActiveSession(session)) return SessionTiming.ACTIVE;
  if (isPastSession(session)) return SessionTiming.PAST;
  if (isFutureSession(session)) return SessionTiming.FUTURE;
  return SessionTiming.UNKNOWN;
};

export const filterActiveSessions = (sessions: Session[]): Session[] =>
  sessions.filter(session => isActiveSession(session));

export const filterUpcomingSessions = (sessions: Session[]): Session[] =>
  sessions.filter(session => isUpcomingSession(session));

export const filterFutureSessions = (sessions: Session[]): Session[] =>
  sessions.filter(session => isFutureSession(session));

export const filterPastSessions = (sessions: Session[]): Session[] =>
  sessions.filter(session => isPastSession(session));

export const filterSameDaySessions = (sessions: Session[]): Session[] =>
  sessions.filter(session => isSameDaySession(session));

export const filterSameMonthSessions = (sessions: Session[]): Session[] =>
  sessions.filter(session => isSameMonthSession(session));

export const isCancelledSession = (session: Session): boolean =>
  [SessionStatus.Cancelled, SessionStatus.NoShow].includes(session.status);

export const isCompletedSession = (session: Session): boolean =>
  session.status === SessionStatus.Completed;
